// ============================================
// pages/Salon/SalonExpenses.js
// Salon Expense Tracker — Add, History, Summary
// ============================================
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../common/context/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SALON_API_URL } from '../../api/config';
import {
  Plus, X, Edit2, Trash2, Search, Wallet,
  Loader2, Calendar,  User, FileText,
  TrendingDown, ChevronDown, ChevronUp, Filter,
  ReceiptText, ArrowUpCircle,
} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = [
  'Rent', 'Salary', 'Utilities', 'Products', 'Equipment',
  'Marketing', 'Maintenance', 'Food', 'Transport', 'Other',
];

const CAT_COLORS = {
  Rent:        { bg: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',       dot: 'bg-blue-500'    },
  Salary:      { bg: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',    dot: 'bg-green-500'   },
  Utilities:   { bg: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300', dot: 'bg-yellow-500' },
  Products:    { bg: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',        dot: 'bg-pink-500'    },
  Equipment:   { bg: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', dot: 'bg-orange-500' },
  Marketing:   { bg: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300', dot: 'bg-purple-500' },
  Maintenance: { bg: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',        dot: 'bg-cyan-500'    },
  Food:        { bg: 'bg-lime-100 text-lime-700 dark:bg-lime-900/40 dark:text-lime-300',         dot: 'bg-lime-500'   },
  Transport:   { bg: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300', dot: 'bg-indigo-500' },
  Other:       { bg: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',            dot: 'bg-gray-400'   },
};

const EMPTY_FORM = {
  title:        '',
  category:     '',
  amount:       '',
  expense_date: new Date().toISOString().split('T')[0],
  paid_to:      '',
  notes:        '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(n) {
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function today() { return new Date().toISOString().split('T')[0]; }
function monthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

// Group expenses by date string
function groupByDate(expenses) {
  const groups = {};
  expenses.forEach(e => {
    const key = e.expense_date?.split('T')[0] || e.expense_date;
    if (!groups[key]) groups[key] = [];
    groups[key].push(e);
  });
  return groups;
}

function dateLabel(dateStr) {
  const t  = today();
  const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
  const yStr = yesterday.toISOString().split('T')[0];
  if (dateStr === t)    return 'Today';
  if (dateStr === yStr) return 'Yesterday';
  return formatDate(dateStr);
}

// ─── Add / Edit Modal ─────────────────────────────────────────────────────────

function ExpenseModal({ mode, expense, onClose, onSave }) {
  const [form,   setForm]   = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (mode === 'edit' && expense) {
      setForm({
        title:        expense.title        || '',
        category:     expense.category     || '',
        amount:       String(expense.amount || ''),
        expense_date: expense.expense_date?.split('T')[0] || today(),
        paid_to:      expense.paid_to      || '',
        notes:        expense.notes        || '',
      });
    }
  }, [mode, expense]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.title.trim())                                         e.title    = 'Expense name is required';
    if (!form.amount || isNaN(form.amount) || Number(form.amount) <= 0) e.amount = 'Valid amount is required';
    if (!form.category)                                             e.category = 'Select a category';
    if (!form.expense_date)                                         e.expense_date = 'Date is required';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      await onSave({
        title:        form.title.trim(),
        category:     form.category,
        amount:       Number(form.amount),
        expense_date: form.expense_date,
        paid_to:      form.paid_to.trim() || null,
        notes:        form.notes.trim()   || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
          <div>
            <h2 className="text-lg font-black text-gray-900 dark:text-white">
              {mode === 'add' ? '💸 Add Expense' : '✏️ Edit Expense'}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Record your salon expense below</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Expense Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
              Expense Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <ReceiptText size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className={`w-full bg-gray-50 dark:bg-slate-800 border rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all ${errors.title ? 'border-red-400' : 'border-gray-200 dark:border-slate-700'}`}
                placeholder="e.g. Electricity Bill, Staff Salary..."
                value={form.title}
                onChange={e => set('title', e.target.value)}
              />
            </div>
            {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
          </div>

          {/* Amount */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
              Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-base">₹</span>
              <input
                type="number" min="0" step="0.01"
                className={`w-full bg-gray-50 dark:bg-slate-800 border rounded-xl pl-9 pr-4 py-3 text-lg font-bold text-gray-900 dark:text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 ${errors.amount ? 'border-red-400' : 'border-gray-200 dark:border-slate-700'}`}
                placeholder="0.00"
                value={form.amount}
                onChange={e => set('amount', e.target.value)}
              />
            </div>
            {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
              Category <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(cat => {
                const color = CAT_COLORS[cat] || CAT_COLORS.Other;
                return (
                  <button key={cat} onClick={() => set('category', cat)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold border-2 transition-all ${
                      form.category === cat
                        ? `${color.bg} border-current`
                        : 'bg-gray-50 dark:bg-slate-800 text-gray-500 dark:text-gray-400 border-transparent hover:border-gray-300 dark:hover:border-slate-600'
                    }`}
                  >{cat}</button>
                );
              })}
            </div>
            {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
          </div>

          {/* Date + Paid To in a row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  max={today()}
                  className={`w-full bg-gray-50 dark:bg-slate-800 border rounded-xl pl-8 pr-3 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 ${errors.expense_date ? 'border-red-400' : 'border-gray-200 dark:border-slate-700'}`}
                  value={form.expense_date}
                  onChange={e => set('expense_date', e.target.value)}
                />
              </div>
              {errors.expense_date && <p className="text-red-500 text-xs mt-1">{errors.expense_date}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                Paid To <span className="font-normal normal-case text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <User size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl pl-8 pr-3 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Vendor / Person"
                  value={form.paid_to}
                  onChange={e => set('paid_to', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
              Notes <span className="font-normal normal-case text-gray-400">(optional)</span>
            </label>
            <div className="relative">
              <FileText size={13} className="absolute left-3 top-3 text-gray-400" />
              <textarea
                rows={3}
                className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl pl-8 pr-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                placeholder="Additional details..."
                value={form.notes}
                onChange={e => set('notes', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 dark:border-slate-800 flex gap-3 flex-shrink-0">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Wallet size={15} />}
            {saving ? 'Saving...' : mode === 'add' ? 'Record Expense' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Modal ─────────────────────────────────────────────────────────────

function DeleteModal({ expense, onClose, onConfirm, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col gap-4">
        <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-2xl flex items-center justify-center">
          <Trash2 size={22} className="text-red-500" />
        </div>
        <div>
          <h3 className="font-black text-gray-900 dark:text-white">Delete Expense?</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Remove <strong className="text-gray-700 dark:text-gray-300">"{expense.title}"</strong> ({formatCurrency(expense.amount)})? This cannot be undone.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-semibold text-gray-600 hover:bg-gray-50 dark:hover:bg-slate-800">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Expense Row ──────────────────────────────────────────────────────────────

function ExpenseRow({ expense, onEdit, onDelete }) {
  const color = CAT_COLORS[expense.category] || CAT_COLORS.Other;
  return (
    <div className="flex items-center gap-3 py-3 border-b border-gray-50 dark:border-slate-800 last:border-0 group">
      {/* Dot */}
      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${color.dot}`} />

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{expense.title}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${color.bg}`}>{expense.category}</span>
          {expense.paid_to && (
            <span className="text-xs text-gray-400 flex items-center gap-0.5">
              <User size={10} /> {expense.paid_to}
            </span>
          )}
          {expense.notes && (
            <span className="text-xs text-gray-400 truncate max-w-[120px]" title={expense.notes}>
              {expense.notes}
            </span>
          )}
        </div>
      </div>

      {/* Amount */}
      <span className="font-black text-red-500 dark:text-red-400 text-sm flex-shrink-0">
        -{formatCurrency(expense.amount)}
      </span>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        <button onClick={() => onEdit(expense)}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-400 hover:text-gray-600 transition-colors">
          <Edit2 size={13} />
        </button>
        <button onClick={() => onDelete(expense)}
          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition-colors">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SalonExpenses() {
  const { userToken: token } = useAuth();

  const [expenses,     setExpenses]     = useState([]);
  const [summary,      setSummary]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [summaryLoad,  setSummaryLoad]  = useState(true);
  const [searchQuery,  setSearchQuery]  = useState('');
  const [filterCat,    setFilterCat]    = useState('All');
  const [dateRange,    setDateRange]    = useState('month'); // today | week | month | custom
  const [fromDate,     setFromDate]     = useState(monthStart());
  const [toDate,       setToDate]       = useState(today());
  const [showFilters,  setShowFilters]  = useState(false);
  const [modal,        setModal]        = useState(null); // {type, expense?}
  const [deleting,     setDeleting]     = useState(false);

  const headers = {
    Authorization:  `Bearer ${token}`,
    'Content-Type': 'application/json',
    Accept:         'application/json',
  };

  // ── Date range presets ────────────────────────────────────────────────────

  const applyRange = useCallback((range) => {
    setDateRange(range);
    const t = today();
    if (range === 'today') { setFromDate(t); setToDate(t); }
    else if (range === 'week') {
      const d = new Date(); d.setDate(d.getDate() - 6);
      setFromDate(d.toISOString().split('T')[0]); setToDate(t);
    } else if (range === 'month') {
      setFromDate(monthStart()); setToDate(t);
    }
  }, []);

  // ── Fetch expenses ────────────────────────────────────────────────────────

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
      if (filterCat !== 'All') params.set('category', filterCat);

      const res  = await fetch(`${SALON_API_URL}/expenses?${params}`, { headers });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to fetch');
      setExpenses(data.data);
    } catch (err) {
      toast.error(err.message || 'Could not load expenses');
    } finally {
      setLoading(false);
    }
  }, [token, fromDate, toDate, filterCat]); // eslint-disable-line

  // ── Fetch summary ─────────────────────────────────────────────────────────

  const fetchSummary = useCallback(async () => {
    setSummaryLoad(true);
    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
      const res  = await fetch(`${SALON_API_URL}/expenses/summary?${params}`, { headers });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed');
      setSummary(data.data);
    } catch (err) {
      console.error('Summary fetch failed:', err);
    } finally {
      setSummaryLoad(false);
    }
  }, [token, fromDate, toDate]); // eslint-disable-line

  useEffect(() => {
    if (token) { fetchExpenses(); fetchSummary(); }
  }, [fetchExpenses, fetchSummary, token]);

  // ── CRUD ──────────────────────────────────────────────────────────────────

  const handleCreate = async (payload) => {
    const res  = await fetch(`${SALON_API_URL}/expenses`, { method: 'POST', headers, body: JSON.stringify(payload) });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || 'Failed to create');
    toast.success('Expense recorded 💸');
    setModal(null);
    fetchExpenses(); fetchSummary();
  };

  const handleUpdate = async (payload) => {
    const res  = await fetch(`${SALON_API_URL}/expenses/${modal.expense.id}`, { method: 'PUT', headers, body: JSON.stringify(payload) });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || 'Failed to update');
    toast.success('Expense updated ✅');
    setModal(null);
    fetchExpenses(); fetchSummary();
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res  = await fetch(`${SALON_API_URL}/expenses/${modal.expense.id}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed');
      toast.success('Expense deleted');
      setModal(null);
      fetchExpenses(); fetchSummary();
    } catch (err) {
      toast.error(err.message || 'Could not delete');
    } finally {
      setDeleting(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const filtered = expenses.filter(e => {
    const q = searchQuery.toLowerCase();
    return e.title?.toLowerCase().includes(q) ||
           e.category?.toLowerCase().includes(q) ||
           e.paid_to?.toLowerCase().includes(q);
  });

  const todayTotal = expenses
    .filter(e => (e.expense_date?.split('T')[0] || e.expense_date) === today())
    .reduce((s, e) => s + Number(e.amount), 0);

  const grouped = groupByDate(filtered);
  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 min-h-full">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-2">
            <Wallet size={22} className="text-red-500" />
            Expenses
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Track your salon's daily expenses
          </p>
        </div>
        <button
          onClick={() => setModal({ type: 'add' })}
          className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-red-200 dark:shadow-red-900/30 transition-all hover:scale-105"
        >
          <Plus size={16} /> Add Expense
        </button>
      </div>

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="col-span-2 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl p-5 text-white">
          <p className="text-sm font-medium opacity-80">Total This Period</p>
          <p className="text-3xl font-black mt-1">
            {summaryLoad ? '...' : formatCurrency(summary?.total || 0)}
          </p>
          <p className="text-xs opacity-70 mt-1">{summary?.count || 0} expenses · {fromDate} to {toDate}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 border border-gray-100 dark:border-slate-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Today's Spend</p>
          <p className="text-xl font-black text-orange-500 dark:text-orange-400 mt-1">{formatCurrency(todayTotal)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{expenses.filter(e => (e.expense_date?.split('T')[0] || e.expense_date) === today()).length} entries</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 border border-gray-100 dark:border-slate-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Top Category</p>
          {summaryLoad ? (
            <p className="text-xl font-black text-gray-300 mt-1">...</p>
          ) : summary?.byCategory?.[0] ? (
            <>
              <p className="text-sm font-black text-gray-800 dark:text-white mt-1">{summary.byCategory[0].category}</p>
              <p className="text-xs text-red-500 font-semibold">{formatCurrency(summary.byCategory[0].total)}</p>
            </>
          ) : (
            <p className="text-sm text-gray-400 mt-1">—</p>
          )}
        </div>
      </div>

      {/* ── Category breakdown bar ── */}
      {!summaryLoad && summary?.byCategory?.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-5 mb-6">
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-3">Breakdown by Category</p>
          <div className="space-y-2.5">
            {summary.byCategory.map(cat => {
              const pct = summary.total > 0 ? (cat.total / summary.total) * 100 : 0;
              const color = CAT_COLORS[cat.category] || CAT_COLORS.Other;
              return (
                <div key={cat.category}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full inline-block ${color.dot}`} />
                      {cat.category}
                      <span className="text-gray-400 font-normal">({cat.count})</span>
                    </span>
                    <span className="font-bold text-gray-700 dark:text-gray-300">{formatCurrency(cat.total)}</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${color.dot}`}
                      style={{ width: `${pct}%`, transition: 'width 0.6s ease' }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 mb-6">

        {/* Top row */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full pl-8 pr-4 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Search expenses..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Date range presets */}
          <div className="flex gap-1.5">
            {[
              { val: 'today', label: 'Today' },
              { val: 'week',  label: 'Week'  },
              { val: 'month', label: 'Month' },
              { val: 'custom', label: 'Custom' },
            ].map(opt => (
              <button key={opt.val} onClick={() => applyRange(opt.val)}
                className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                  dateRange === opt.val
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-600'
                }`}
              >{opt.label}</button>
            ))}
          </div>

          {/* Filter toggle */}
          <button onClick={() => setShowFilters(v => !v)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100">
            <Filter size={12} /> Filters {showFilters ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>

        {/* Expanded filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700 flex flex-wrap gap-4 items-end">
            {/* Custom date range */}
            {dateRange === 'custom' && (
              <div className="flex gap-3 items-center">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">From</label>
                  <input type="date" value={fromDate} max={toDate}
                    onChange={e => setFromDate(e.target.value)}
                    className="border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-1.5 text-xs bg-gray-50 dark:bg-slate-700 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">To</label>
                  <input type="date" value={toDate} min={fromDate} max={today()}
                    onChange={e => setToDate(e.target.value)}
                    className="border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-1.5 text-xs bg-gray-50 dark:bg-slate-700 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
            )}

            {/* Category filter */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Category</label>
              <div className="flex flex-wrap gap-1.5">
                {['All', ...CATEGORIES].map(cat => (
                  <button key={cat} onClick={() => setFilterCat(cat)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                      filterCat === cat
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600'
                    }`}
                  >{cat}</button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Expense History ── */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700">
          <h2 className="text-sm font-black text-gray-700 dark:text-white flex items-center gap-2">
            <ArrowUpCircle size={16} className="text-red-500" />
            Expense History
          </h2>
          <span className="text-xs text-gray-400">{filtered.length} entries</span>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 size={24} className="animate-spin text-red-500" />
            <p className="text-sm text-gray-400">Loading expenses...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-14 h-14 bg-red-50 dark:bg-red-900/20 rounded-2xl flex items-center justify-center">
              <Wallet size={24} className="text-red-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
              {searchQuery || filterCat !== 'All' ? 'No expenses match your filters' : 'No expenses recorded yet'}
            </p>
            {!searchQuery && filterCat === 'All' && (
              <button onClick={() => setModal({ type: 'add' })}
                className="text-red-500 dark:text-red-400 text-sm font-semibold flex items-center gap-1 hover:underline">
                <Plus size={14} /> Record your first expense
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-slate-800">
            {sortedDates.map(date => {
              const dayItems  = grouped[date];
              const dayTotal  = dayItems.reduce((s, e) => s + Number(e.amount), 0);
              return (
                <div key={date}>
                  {/* Date group header */}
                  <div className="flex items-center justify-between px-5 py-2.5 bg-gray-50 dark:bg-slate-900/50">
                    <div className="flex items-center gap-2">
                      <Calendar size={13} className="text-gray-400" />
                      <span className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                        {dateLabel(date)}
                      </span>
                      <span className="text-xs text-gray-400">{date !== today() && date !== (() => { const y = new Date(); y.setDate(y.getDate()-1); return y.toISOString().split('T')[0]; })() ? '' : ''}</span>
                    </div>
                    <span className="text-xs font-black text-red-500 dark:text-red-400">
                      {formatCurrency(dayTotal)}
                    </span>
                  </div>

                  {/* Expense rows */}
                  <div className="px-5">
                    {dayItems.map(exp => (
                      <ExpenseRow
                        key={exp.id}
                        expense={exp}
                        onEdit={e => setModal({ type: 'edit', expense: e })}
                        onDelete={e => setModal({ type: 'delete', expense: e })}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Grand total footer */}
            <div className="flex items-center justify-between px-5 py-4 bg-red-50 dark:bg-red-900/10">
              <span className="text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <TrendingDown size={15} className="text-red-500" />
                Period Total
              </span>
              <span className="text-base font-black text-red-600 dark:text-red-400">
                {formatCurrency(filtered.reduce((s, e) => s + Number(e.amount), 0))}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {modal?.type === 'add' && (
        <ExpenseModal mode="add" onClose={() => setModal(null)} onSave={handleCreate} />
      )}
      {modal?.type === 'edit' && (
        <ExpenseModal mode="edit" expense={modal.expense} onClose={() => setModal(null)} onSave={handleUpdate} />
      )}
      {modal?.type === 'delete' && (
        <DeleteModal expense={modal.expense} onClose={() => setModal(null)} onConfirm={handleDelete} loading={deleting} />
      )}
    </div>
  );
}